
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Trash2, Edit, Plus } from "lucide-react";

interface VideoOfTheDay {
  id: string;
  youtube_url: string;
  title: string | null;
  description: string | null;
  is_active: boolean;
}

const VideoOfTheDayManager = () => {
  const [videos, setVideos] = useState<VideoOfTheDay[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    youtube_url: "",
    title: "",
    description: ""
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const { data, error } = await supabase
        .from('video_of_the_day')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setVideos(data || []);
    } catch (error) {
      console.error('Error fetching videos:', error);
      toast({
        title: "Error",
        description: "Failed to fetch videos",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (editingId) {
        const { error } = await supabase
          .from('video_of_the_day')
          .update({
            youtube_url: formData.youtube_url,
            title: formData.title || null,
            description: formData.description || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingId);

        if (error) throw error;
        toast({
          title: "Success",
          description: "Video updated successfully",
        });
      } else {
        // First, deactivate all existing videos since only one should be active
        await supabase
          .from('video_of_the_day')
          .update({ is_active: false })
          .neq('id', '00000000-0000-0000-0000-000000000000');

        const { error } = await supabase
          .from('video_of_the_day')
          .insert({
            youtube_url: formData.youtube_url,
            title: formData.title || null,
            description: formData.description || null,
            is_active: true
          });

        if (error) throw error;
        toast({
          title: "Success",
          description: "Video of the day set successfully",
        });
      }

      setFormData({ youtube_url: "", title: "", description: "" });
      setEditingId(null);
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      toast({
        title: "Error",
        description: "Failed to save video",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (video: VideoOfTheDay) => {
    setFormData({
      youtube_url: video.youtube_url,
      title: video.title || "",
      description: video.description || ""
    });
    setEditingId(video.id);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('video_of_the_day')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({
        title: "Success",
        description: "Video deleted successfully",
      });
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast({
        title: "Error",
        description: "Failed to delete video",
        variant: "destructive",
      });
    }
  };

  const setAsActive = async (id: string) => {
    try {
      // Deactivate all videos first
      await supabase
        .from('video_of_the_day')
        .update({ is_active: false })
        .neq('id', '00000000-0000-0000-0000-000000000000');

      // Activate the selected video
      const { error } = await supabase
        .from('video_of_the_day')
        .update({ is_active: true })
        .eq('id', id);

      if (error) throw error;
      toast({
        title: "Success",
        description: "Video set as active",
      });
      fetchVideos();
    } catch (error) {
      console.error('Error setting active video:', error);
      toast({
        title: "Error",
        description: "Failed to set active video",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {editingId ? "Edit Video of the Day" : "Add Video of the Day"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="youtube_url">YouTube URL *</Label>
              <Input
                id="youtube_url"
                type="url"
                value={formData.youtube_url}
                onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                placeholder="https://www.youtube.com/watch?v=..."
                required
              />
            </div>
            <div>
              <Label htmlFor="title">Title (Optional)</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Video title"
              />
            </div>
            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Video description"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : editingId ? "Update Video" : "Add Video"}
              </Button>
              {editingId && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditingId(null);
                    setFormData({ youtube_url: "", title: "", description: "" });
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Existing Videos</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {videos.map((video) => (
              <div
                key={video.id}
                className={`p-4 border rounded-lg ${
                  video.is_active ? "bg-green-50 border-green-200" : "bg-gray-50"
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-medium">{video.title || "Untitled"}</h4>
                    <p className="text-sm text-gray-600 mt-1">{video.youtube_url}</p>
                    {video.description && (
                      <p className="text-sm text-gray-500 mt-1">{video.description}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span
                        className={`text-xs px-2 py-1 rounded ${
                          video.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {video.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    {!video.is_active && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAsActive(video.id)}
                      >
                        Set Active
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(video)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(video.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {videos.length === 0 && (
              <p className="text-gray-500 text-center py-8">No videos added yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VideoOfTheDayManager;
